import { useMemo } from 'react';
import { useTheme } from '@mui/material';
import { ConditionPartitionedCount } from '@retail/dashboard/types';
import { useDashboardTFunction } from '@retail/dashboard/i18n';
import dayjs from 'dayjs';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

interface Props {
  data: ConditionPartitionedCount[];
  height?: number;
}

export const AgreementPricesBarChart = ({ data, height = 220 }: Props) => {
  const theme = useTheme();
  const { palette } = theme.vars;
  const t = useDashboardTFunction();

  const chartData = useMemo(
    () => data.map((row) => ({ date: dayjs(row.date).format('l'), count: row.count })),
    [data]
  );

  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart
        layout="vertical"
        data={chartData}
        margin={{ top: 8, right: 8, left: 8, bottom: 0 }}
      >
        <CartesianGrid horizontal={false} stroke={palette.divider} />
        <XAxis
          type="number"
          allowDecimals={false}
          tick={{ fontSize: 12, fill: palette.text.secondary }}
          tickLine={false}
          axisLine={false}
        />
        <YAxis
          type="category"
          dataKey="date"
          width={80}
          tick={{ fontSize: 12, fill: palette.text.secondary }}
          tickLine={false}
          axisLine={{ stroke: palette.divider }}
        />
        <Tooltip
          cursor={{ fill: palette.action.hover }}
          formatter={(value) => [value, t('dashboard.count')]}
          contentStyle={{
            background: palette.background.paper,
            borderRadius: theme.shape.borderRadius,
            border: `1px solid ${palette.divider}`,
            color: palette.text.primary,
          }}
        />
        <Bar
          dataKey="count"
          name={t('dashboard.count')}
          fill={palette.primary.main}
          radius={[0, 4, 4, 0]}
          maxBarSize={48}
        />
      </BarChart>
    </ResponsiveContainer>
  );
};
